import { h } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import store from 'dashboard/store';
import AppContainer from 'dashboard/routes/dashboard/Dashboard.vue';
import SettingsContent from 'dashboard/routes/dashboard/settings/Wrapper.vue';
import pluginMetadata from '../../../plugin.json';
import WooCommerceBrandIcon from './components/WooCommerceBrandIcon.vue';
import WooCommerceConversationPanel from './components/widgets/conversation/WooCommerceConversationPanel.vue';
import WooCommerceSidepanelButton from './components/widgets/conversation/WooCommerceSidepanelButton.vue';
import WooCommerceIntegration from './routes/settings/integrations/WooCommerce.vue';
import WooCommerceOrdersIndex from './routes/woocommerce/WooCommerceOrdersIndex.vue';
import WooCommerceAbandonedCheckoutsIndex from './routes/woocommerce/WooCommerceAbandonedCheckoutsIndex.vue';
import WooCommerceOrderDetails from './routes/woocommerce/WooCommerceOrderDetails.vue';

const WOO_COMMERCE_FEATURE_FLAG = 'woocommerce_integration';
const automationContextKey = pluginMetadata.config.AUTOMATION_CONTEXT_KEY;
const automationContextLabel = pluginMetadata.config.AUTOMATION_CONTEXT_LABEL;
const automationContextIcon = pluginMetadata.config.AUTOMATION_CONTEXT_ICON;

const buildTextFilterOperators = () => [
  { value: 'equal_to', label: 'Equal to' },
  { value: 'not_equal_to', label: 'Not equal to' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'is_present', label: 'Is present' },
  { value: 'is_not_present', label: 'Is not present' },
];

const automationExtension = {
  entityAttributes: {
    contact: {
      woocommerce_event_name: {
        name: 'WOOCOMMERCE_EVENT_NAME',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      woocommerce_order_id: {
        name: 'WOOCOMMERCE_ORDER_ID',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      woocommerce_order_number: {
        name: 'WOOCOMMERCE_ORDER_NUMBER',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      woocommerce_order_status: {
        name: 'WOOCOMMERCE_ORDER_STATUS',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      woocommerce_payment_status: {
        name: 'WOOCOMMERCE_PAYMENT_STATUS',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      woocommerce_total: {
        name: 'WOOCOMMERCE_TOTAL',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      woocommerce_currency: {
        name: 'WOOCOMMERCE_CURRENCY',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
    },
  },
  eventContexts: {
    [automationContextKey]: {
      label: automationContextLabel,
      icon: automationContextIcon,
      events: pluginMetadata.config.AUTOMATION_EVENT_KEYS,
    },
  },
  eventEntities: pluginMetadata.config.AUTOMATION_EVENT_KEYS.reduce(
    (result, key) => ({
      ...result,
      [key]: ['contact'],
    }),
    {}
  ),
  actionsByEntity: {
    contact: [
      'send_webhook_event',
      'delay_next_action',
      'delay_next_action_range',
    ],
  },
  automationRuleEvents: pluginMetadata.config.AUTOMATION_EVENT_KEYS.map(
    key => ({
      key,
      value: key.toUpperCase(),
      context: automationContextKey,
    })
  ),
};

const ensurePluginState = () => {
  window.chatwoot = window.chatwoot || {};
  window.chatwoot.plugins = window.chatwoot.plugins || {};
  window.chatwoot.plugins.woocommerce =
    window.chatwoot.plugins.woocommerce || {};

  return window.chatwoot.plugins.woocommerce;
};

const mergeAutomationRuleEvents = (baseEvents = [], extensionEvents = []) => {
  const seen = new Map();
  [...baseEvents, ...extensionEvents].forEach(event => {
    if (event?.key) {
      seen.set(event.key, event);
    }
  });

  return Array.from(seen.values());
};

const getWooCommerceIntegration = () => {
  const integrationGetter = store.getters['integrations/getIntegration'];
  return integrationGetter ? integrationGetter('woocommerce', {}) : null;
};

const hasActiveWooCommerceConnection = () => {
  const integration = getWooCommerceIntegration();
  return Boolean(
    Array.isArray(integration?.hooks) && integration.hooks.length > 0
  );
};

const buildWooCommerceSidebarIcon = size =>
  h(WooCommerceBrandIcon, {
    size,
    rounded: false,
  });

const buildWooCommerceSettingsRoute = accountScopedRoute => {
  const router = window.chatwootRouter;

  if (router?.hasRoute?.('settings_integrations_woocommerce')) {
    return accountScopedRoute('settings_integrations_woocommerce');
  }

  return accountScopedRoute('settings_applications_integration', {
    integration_id: 'woocommerce',
  });
};

const buildWooCommerceRoute = (
  accountScopedRoute,
  routeName,
  params = {},
  fallbackRoute = null
) => {
  const router = window.chatwootRouter;

  if (router?.hasRoute?.(routeName)) {
    return accountScopedRoute(routeName, params);
  }

  return fallbackRoute || buildWooCommerceSettingsRoute(accountScopedRoute);
};

const buildWooCommerceSidebarChildren = payload => {
  const settingsRoute = buildWooCommerceSettingsRoute(
    payload.accountScopedRoute
  );

  return [
    {
      name: 'WooCommerce Orders',
      label: payload.t?.('WOOCOMMERCE_PAGES.SIDEBAR_ORDERS') || 'Pedidos',
      icon: 'i-lucide-receipt-text',
      activeOn: ['woocommerce_orders', 'woocommerce_order_details'],
      to: buildWooCommerceRoute(
        payload.accountScopedRoute,
        'woocommerce_orders',
        {},
        settingsRoute
      ),
    },
    {
      name: 'WooCommerce Abandoned Carts',
      label:
        payload.t?.('WOOCOMMERCE_PAGES.SIDEBAR_ABANDONED') ||
        'Carrinhos abandonados',
      icon: 'i-lucide-shopping-basket',
      activeOn: ['woocommerce_abandoned_checkouts'],
      to: buildWooCommerceRoute(
        payload.accountScopedRoute,
        'woocommerce_abandoned_checkouts',
        {},
        settingsRoute
      ),
    },
  ];
};

const isWooCommerceFeatureEnabled = accountId => {
  const featureGetter = store.getters['accounts/isFeatureEnabledonAccount'];
  return featureGetter
    ? featureGetter(Number(accountId), WOO_COMMERCE_FEATURE_FLAG)
    : false;
};

const hasWooCommerceAccess = accountId => {
  const integration = getWooCommerceIntegration();
  return Boolean(
    isWooCommerceFeatureEnabled(accountId) ||
      integration?.enabled ||
      (Array.isArray(integration?.hooks) && integration.hooks.length > 0)
  );
};

const buildWooCommerceMenuItem = payload => {
  const integration = getWooCommerceIntegration();
  const hooks = Array.isArray(integration?.hooks) ? integration.hooks : [];

  return hooks.length > 0
    ? {
        name: 'WooCommerce',
        label: payload.t?.('WOOCOMMERCE_PAGES.SIDEBAR_TITLE') || 'WooCommerce',
        icon: buildWooCommerceSidebarIcon(16),
        children: buildWooCommerceSidebarChildren(payload),
      }
    : {
        name: 'WooCommerce',
        label: payload.t?.('WOOCOMMERCE_PAGES.SIDEBAR_TITLE') || 'WooCommerce',
        icon: buildWooCommerceSidebarIcon(16),
        to: buildWooCommerceSettingsRoute(payload.accountScopedRoute),
        activeOn: [
          'settings_integrations_woocommerce',
          'settings_applications_integration',
          'woocommerce_orders',
          'woocommerce_order_details',
          'woocommerce_abandoned_checkouts',
        ],
      };
};

const ensureWooCommerceAccess = to => {
  const routeAccountId = Number(to.params.accountId || to.params.account_id);
  if (hasWooCommerceAccess(routeAccountId)) {
    return true;
  }

  return {
    name: 'settings_applications',
    params: { accountId: routeAccountId },
  };
};

const registerIntegrationRoute = () => {
  const router = window.chatwootRouter;
  if (
    !router?.addRoute ||
    router?.hasRoute?.('settings_integrations_woocommerce')
  ) {
    return;
  }

  router.addRoute({
    path: frontendURL('accounts/:accountId'),
    component: AppContainer,
    children: [
      {
        path: 'settings/integrations/woocommerce',
        component: SettingsContent,
        props: {
          headerTitle: 'INTEGRATION_SETTINGS.HEADER',
          icon: 'flash-on',
          showBackButton: true,
          backUrl: { name: 'settings_applications' },
        },
        children: [
          {
            path: '',
            name: 'settings_integrations_woocommerce',
            component: WooCommerceIntegration,
            beforeEnter: ensureWooCommerceAccess,
            meta: {
              featureFlag: WOO_COMMERCE_FEATURE_FLAG,
              permissions: ['administrator'],
            },
            props: route => ({ error: route.query.error }),
          },
        ],
      },
      {
        path: 'woocommerce/orders/:hookId?',
        name: 'woocommerce_orders',
        component: WooCommerceOrdersIndex,
        beforeEnter: ensureWooCommerceAccess,
        meta: {
          featureFlag: WOO_COMMERCE_FEATURE_FLAG,
          permissions: ['administrator'],
        },
      },
      {
        path: 'woocommerce/orders/details/:orderRecordId',
        name: 'woocommerce_order_details',
        component: WooCommerceOrderDetails,
        beforeEnter: ensureWooCommerceAccess,
        meta: {
          featureFlag: WOO_COMMERCE_FEATURE_FLAG,
          permissions: ['administrator'],
        },
      },
      {
        path: 'woocommerce/abandoned-checkouts/:hookId?',
        name: 'woocommerce_abandoned_checkouts',
        component: WooCommerceAbandonedCheckoutsIndex,
        beforeEnter: ensureWooCommerceAccess,
        meta: {
          featureFlag: WOO_COMMERCE_FEATURE_FLAG,
          permissions: ['administrator'],
        },
      },
    ],
  });
};

const rematchCurrentRoute = () => {
  const router = window.chatwootRouter;
  const currentRoute = router?.currentRoute?.value;
  if (!router?.replace || !currentRoute) {
    return;
  }

  const isGenericIntegrationRoute =
    currentRoute.name === 'settings_applications_integration' &&
    currentRoute.params?.integration_id === 'woocommerce';
  const isWooCommercePath = currentRoute.path?.includes(
    '/settings/integrations/woocommerce'
  );
  if (!isGenericIntegrationRoute && !isWooCommercePath) {
    return;
  }

  const accountId =
    currentRoute.params?.accountId || currentRoute.params?.account_id;
  if (!accountId) {
    return;
  }

  Promise.resolve().then(() =>
    router
      .replace({
        name: 'settings_integrations_woocommerce',
        params: { accountId },
        query: currentRoute.query,
        hash: currentRoute.hash,
      })
      .catch(() => {})
  );
};

export default {
  install(app, { PluginSystem } = {}) {
    const pluginState = ensurePluginState();
    pluginState.config = pluginMetadata.config;

    registerIntegrationRoute();
    rematchCurrentRoute();

    if (PluginSystem?.registerComponent) {
      PluginSystem.registerComponent('ConversationSidepanelButtons', {
        name: 'WooCommerce Sidepanel Button',
        component: WooCommerceSidepanelButton,
      });

      PluginSystem.registerComponent('DashboardOverlays', {
        name: 'WooCommerce Conversation Panel',
        component: WooCommerceConversationPanel,
      });
    }

    if (PluginSystem?.registerHook) {
      const {
        contact: automationContactAttributes = {},
        ...automationEntityAttributes
      } = automationExtension.entityAttributes || {};

      PluginSystem.registerHook('automation-enhanced:config', config => {
        if (!hasActiveWooCommerceConnection()) {
          return config;
        }

        return {
          ...config,
          entityAttributes: {
            ...config.entityAttributes,
            ...automationEntityAttributes,
            contact: {
              ...(config.entityAttributes?.contact || {}),
              ...automationContactAttributes,
            },
          },
          eventContexts: {
            ...config.eventContexts,
            ...automationExtension.eventContexts,
          },
          eventEntities: {
            ...config.eventEntities,
            ...automationExtension.eventEntities,
          },
          actionsByEntity: {
            ...config.actionsByEntity,
            ...automationExtension.actionsByEntity,
            contact: [
              ...new Set([
                ...(config.actionsByEntity?.contact || []),
                ...(automationExtension.actionsByEntity?.contact || []),
              ]),
            ],
          },
          automationRuleEvents: mergeAutomationRuleEvents(
            config.automationRuleEvents,
            automationExtension.automationRuleEvents
          ),
        };
      });

      PluginSystem.registerHook('sidebar:menu-items', payload => {
        if (!payload?.items || !Array.isArray(payload.items)) {
          return payload;
        }

        const isFeatureEnabled = payload.isFeatureEnabledonAccount?.(
          payload.accountId,
          WOO_COMMERCE_FEATURE_FLAG
        );
        const integration = getWooCommerceIntegration();
        const hasActiveHooks =
          Array.isArray(integration?.hooks) && integration.hooks.length > 0;

        if (!isFeatureEnabled && !integration?.enabled && !hasActiveHooks) {
          return payload;
        }

        const exists = payload.items.some(item => item.name === 'WooCommerce');
        if (exists) {
          return payload;
        }

        const menuItem = buildWooCommerceMenuItem(payload);

        const settingsIndex = payload.items.findIndex(
          item => item.name === 'Settings'
        );

        if (settingsIndex >= 0) {
          payload.items.splice(settingsIndex, 0, menuItem);
        } else {
          payload.items.push(menuItem);
        }

        return payload;
      });
    }

    app.component('WooCommerceIntegration', WooCommerceIntegration);
    app.component('WooCommerceOrdersIndex', WooCommerceOrdersIndex);
    app.component(
      'WooCommerceAbandonedCheckoutsIndex',
      WooCommerceAbandonedCheckoutsIndex
    );

    store
      .dispatch('integrations/get')
      .then(() => {
        window.dispatchEvent(new Event('chatwoot:sidebar:refresh'));
      })
      .catch(() => {});
  },
};
